import { useMemo, useState } from "react";
import { mean, fmtN, fmtPct, C } from "./icUtils";

const card = "mk-card p-5";
const RATING_COLORS = { Standard: C.green, "Non-Critical": C.yellow, Critical: C.red };
const ASSESS_COLORS = { Standard: C.green, "Below Standard": C.yellow, Fatal: C.red };

export default function QualityView({ data, slices, onOpenAgent }) {
  const [lobFilter, setLobFilter] = useState("");
  const [qslTl, setQslTl] = useState("");

  const pmt = slices.pmt;
  const qsl = slices.qsl;

  const ratingMix = useMemo(() => {
    const mix = { Standard: 0, "Non-Critical": 0, Critical: 0 };
    pmt.forEach((p) => { if (mix[p.rating] != null) mix[p.rating] += 1; });
    return mix;
  }, [pmt]);
  const pmtAvg = mean(pmt.map((p) => p.score).filter((v) => v != null));

  const lobs = useMemo(() => [...new Set(data.pmt.map((p) => p.lob))].sort(), [data]);

  const subRates = useMemo(() => {
    const rows = lobFilter ? pmt.filter((p) => p.lob === lobFilter) : pmt;
    return data.pmt_sub_names.map((name, i) => {
      const vals = rows.map((p) => p.subs[i]).filter((v) => v != null);
      return { name, rate: vals.length ? (100 * vals.reduce((a, b) => a + b, 0)) / vals.length : null, n: vals.length };
    }).sort((a, b) => (a.rate ?? 101) - (b.rate ?? 101));
  }, [pmt, lobFilter, data.pmt_sub_names]);

  const byLob = useMemo(() => {
    const g = {};
    pmt.forEach((p) => {
      const b = (g[p.lob] = g[p.lob] || { lob: p.lob, evals: 0, agents: new Set(), scores: [], crit: 0 });
      b.evals += 1; b.agents.add(p.c);
      if (p.score != null) b.scores.push(p.score);
      if (p.rating === "Critical") b.crit += 1;
    });
    return Object.values(g).map((b) => ({ ...b, agents: b.agents.size, avg: mean(b.scores), critPct: b.evals ? (100 * b.crit) / b.evals : null }))
      .sort((a, b) => (a.avg ?? 101) - (b.avg ?? 101));
  }, [pmt]);

  const assessMix = useMemo(() => {
    const mix = { Standard: 0, "Below Standard": 0, Fatal: 0 };
    qsl.forEach((q) => { if (mix[q.assessment] != null) mix[q.assessment] += 1; });
    return mix;
  }, [qsl]);
  const qslWA = mean(qsl.filter((q) => q.ch === "WA").map((q) => q.score).filter((v) => v != null));
  const qslLC = mean(qsl.filter((q) => q.ch === "LC").map((q) => q.score).filter((v) => v != null));

  const errorDrivers = useMemo(() => {
    const cnt = {};
    qsl.forEach((q) => q.errors.forEach((e) => {
      const key = e.err || e.cat;
      if (!key) return;
      cnt[key] = cnt[key] || { key, cat: e.cat, n: 0 };
      cnt[key].n += 1;
    }));
    return Object.values(cnt).sort((a, b) => b.n - a.n).slice(0, 12);
  }, [qsl]);

  const qslTls = useMemo(() => [...new Set(qsl.map((q) => q.tl || data.agents[q.c]?.tl).filter((t) => t && t !== "-"))].sort(), [qsl, data]);
  const logRows = useMemo(() => {
    const tlOf = (q) => q.tl || data.agents[q.c]?.tl || "—";
    let rows = qsl;
    if (qslTl) rows = rows.filter((q) => tlOf(q) === qslTl);
    else rows = rows.filter((q) => q.assessment !== "Standard");
    return [...rows].sort((a, b) => ((a.d || "") < (b.d || "") ? 1 : -1));
  }, [qsl, qslTl, data]);
  const logSummary = useMemo(() => {
    if (!qslTl) return null;
    const scores = logRows.map((q) => q.score).filter((v) => v != null);
    return { n: logRows.length, avg: mean(scores), fatal: logRows.filter((q) => q.assessment === "Fatal").length };
  }, [logRows, qslTl]);

  const totalRatings = Object.values(ratingMix).reduce((a, b) => a + b, 0) || 1;
  const totalAssess = Object.values(assessMix).reduce((a, b) => a + b, 0) || 1;

  return (
    <div className="space-y-8">
      <div className="grid gap-5 lg:grid-cols-2">
        <div className={card} data-testid="ic-pmt-mix">
          <p className="font-mono-hud mb-4 text-[10px] uppercase tracking-[0.25em] text-thdim">PMT Rating Mix · internal quality ({pmt.length} evaluations)</p>
          <div className="mb-4 flex items-baseline gap-3">
            <p className="font-mono-hud text-3xl font-semibold" style={{ color: pmtAvg != null && pmtAvg >= data.targets.pmt ? C.green : C.red }}>{fmtPct(pmtAvg)}</p>
            <p className="text-xs text-thdim">average score vs {data.targets.pmt}% target</p>
          </div>
          <div className="mb-3 flex h-3 w-full overflow-hidden rounded-full bg-thhover/10">
            {Object.entries(ratingMix).map(([k, v]) => (
              <div key={k} style={{ width: `${(100 * v) / totalRatings}%`, background: RATING_COLORS[k] }} />
            ))}
          </div>
          <div className="grid grid-cols-3 gap-3">
            {Object.entries(ratingMix).map(([k, v]) => (
              <div key={k} className="rounded-lg bg-thhover/5 p-3">
                <p className="text-xs" style={{ color: RATING_COLORS[k] }}>{k}</p>
                <p className="font-mono-hud mt-1 text-lg font-semibold text-thstrong">{v}</p>
                <p className="font-mono-hud text-[10px] text-thdim">{fmtN((100 * v) / totalRatings, 1)}%</p>
              </div>
            ))}
          </div>
        </div>

        <div className={card} data-testid="ic-qsl-mix">
          <p className="font-mono-hud mb-4 text-[10px] uppercase tracking-[0.25em] text-thdim">QSL External Audit · {qsl.length} audits</p>
          <div className="mb-4 flex flex-wrap items-baseline gap-5">
            <div><p className="font-mono-hud text-2xl font-semibold text-thcyan">{fmtPct(qslWA)}</p><p className="text-[10px] uppercase text-thdim">WhatsApp avg</p></div>
            <div><p className="font-mono-hud text-2xl font-semibold" style={{ color: C.purple }}>{fmtPct(qslLC)}</p><p className="text-[10px] uppercase text-thdim">Live Chat avg</p></div>
            <p className="text-xs text-thdim">Raw mean incl. fatal zeros · target {data.targets.qsl}%</p>
          </div>
          <div className="mb-3 flex h-3 w-full overflow-hidden rounded-full bg-thhover/10">
            {Object.entries(assessMix).map(([k, v]) => (
              <div key={k} style={{ width: `${(100 * v) / totalAssess}%`, background: ASSESS_COLORS[k] }} />
            ))}
          </div>
          <div className="grid grid-cols-3 gap-3">
            {Object.entries(assessMix).map(([k, v]) => (
              <div key={k} className="rounded-lg bg-thhover/5 p-3">
                <p className="text-xs" style={{ color: ASSESS_COLORS[k] }}>{k}</p>
                <p className="font-mono-hud mt-1 text-lg font-semibold text-thstrong">{v}</p>
                <p className="font-mono-hud text-[10px] text-thdim">{fmtN((100 * v) / totalAssess, 1)}%</p>
              </div>
            ))}
          </div>
        </div>
      </div>

      <div className="grid gap-5 lg:grid-cols-2">
        <div className={card} data-testid="ic-sub-params">
          <div className="mb-4 flex items-center justify-between gap-3">
            <p className="font-mono-hud text-[10px] uppercase tracking-[0.25em] text-thdim">PMT Sub-Parameter Pass Rates · weakest first</p>
            <select data-testid="ic-sub-lob-select" value={lobFilter} onChange={(e) => setLobFilter(e.target.value)}
              className="max-w-[200px] rounded-md border border-thborder/10 bg-thinput px-2.5 py-1.5 text-xs text-thstrong focus:border-thcyan focus:outline-none">
              <option value="">All SM lines</option>
              {lobs.map((l) => <option key={l} value={l}>{l}</option>)}
            </select>
          </div>
          <div className="space-y-2.5">
            {subRates.map((s) => (
              <div key={s.name}>
                <div className="mb-1 flex items-center justify-between">
                  <p className="text-xs text-thbody">{s.name}</p>
                  <p className="font-mono-hud text-[10px] text-thmute">{fmtPct(s.rate)} · n={s.n}</p>
                </div>
                <div className="h-2.5 w-full rounded-full bg-thhover/10">
                  <div className="h-2.5 rounded-full transition-all" style={{ width: `${s.rate ?? 0}%`, background: s.rate == null ? C.grey : s.rate >= 80 ? C.green : s.rate >= 60 ? C.yellow : C.red }} />
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className={card} data-testid="ic-error-drivers">
          <p className="font-mono-hud mb-4 text-[10px] uppercase tracking-[0.25em] text-thdim">QSL Top Error Drivers · top 12</p>
          {errorDrivers.length === 0 ? <p className="py-6 text-center text-sm text-thdim">No errors logged in the selected range.</p> : (
            <div className="space-y-2.5">
              {errorDrivers.map((e) => (
                <div key={e.key} className="flex items-center gap-3">
                  <div className="h-2.5 rounded-full" style={{ width: `${(100 * e.n) / errorDrivers[0].n}%`, minWidth: 8, background: C.red, opacity: 0.8 }} />
                  <p className="flex-1 truncate text-xs text-thbody" title={e.key}>{e.key} <span className="text-thdim">· {e.cat}</span></p>
                  <p className="font-mono-hud text-xs text-thmute">{e.n}</p>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      <div className={card} data-testid="ic-pmt-lob">
        <p className="font-mono-hud mb-3 text-[10px] uppercase tracking-[0.25em] text-thdim">PMT by Line of Business</p>
        <div className="overflow-x-auto">
          <table className="w-full text-left">
            <thead><tr className="border-b border-thborder/10">
              {["LOB", "Evals", "Agents", "Avg Score", "Criticals", "Crit %"].map((h, i) => (
                <th key={h} className={`font-mono-hud px-3 py-2 text-[9px] uppercase tracking-[0.12em] text-thdim ${i ? "text-right" : "text-left"}`}>{h}</th>
              ))}
            </tr></thead>
            <tbody>
              {byLob.map((r) => (
                <tr key={r.lob} className="border-b border-thborder/5">
                  <td className="px-3 py-2 text-xs font-medium text-thstrong">{r.lob}</td>
                  <td className="px-3 py-2 text-right font-mono-hud text-xs text-thbody">{r.evals}</td>
                  <td className="px-3 py-2 text-right font-mono-hud text-xs text-thbody">{r.agents}</td>
                  <td className={`px-3 py-2 text-right font-mono-hud text-xs font-semibold ${r.avg == null ? "text-thdim" : r.avg >= 80 ? "text-[#10B981]" : "text-[#F43F5E]"}`}>{fmtPct(r.avg)}</td>
                  <td className="px-3 py-2 text-right font-mono-hud text-xs text-thbody">{r.crit}</td>
                  <td className="px-3 py-2 text-right font-mono-hud text-xs text-thbody">{fmtPct(r.critPct)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <div className={card} data-testid="ic-qsl-log">
        <div className="mb-3 flex flex-wrap items-center justify-between gap-3">
          <div>
            <p className="font-mono-hud text-[10px] uppercase tracking-[0.25em] text-thdim">QSL Audit Log</p>
            <p className="mt-1 text-xs text-thdim">{qslTl ? `Complete history for ${qslTl}'s team` : "Fatal & Below-Standard across all teams — pick a TL for their full history"}</p>
          </div>
          <select data-testid="ic-qsl-tl-select" value={qslTl} onChange={(e) => setQslTl(e.target.value)}
            className="rounded-md border border-thborder/10 bg-thinput px-2.5 py-1.5 text-xs text-thstrong focus:border-thcyan focus:outline-none">
            <option value="">All teams (fatal / below-standard)</option>
            {qslTls.map((t) => <option key={t} value={t}>{t}</option>)}
          </select>
        </div>
        {logSummary && (
          <p className="mb-3 rounded-lg bg-thhover/5 px-3 py-2 text-xs text-thbody">
            Team summary: <span className="font-mono-hud">{logSummary.n}</span> audits · avg <span className="font-mono-hud">{fmtPct(logSummary.avg)}</span> · <span className="font-mono-hud text-[#F43F5E]">{logSummary.fatal}</span> fatal
          </p>
        )}
        <div className="max-h-[460px] overflow-auto">
          <table className="w-full text-left">
            <thead className="sticky top-0 bg-thcard"><tr className="border-b border-thborder/10">
              {["Date", "Agent", "TL", "Channel", "Assessment", "Score", "Errors", "Tag"].map((h) => (
                <th key={h} className="font-mono-hud whitespace-nowrap px-3 py-2 text-[9px] uppercase tracking-[0.12em] text-thdim">{h}</th>
              ))}
            </tr></thead>
            <tbody>
              {logRows.map((q, i) => (
                <tr key={`${q.guid || i}`} data-testid={`ic-qsl-log-row-${i}`} onClick={() => onOpenAgent(q.c)} className="cursor-pointer border-b border-thborder/5 hover:bg-thhover/5">
                  <td className="whitespace-nowrap px-3 py-2 font-mono-hud text-xs text-thmute">{q.d || "—"}{q.approx && <span className="text-thdim" title="Week-level date placed on ISO Monday">≈</span>}</td>
                  <td className="max-w-[160px] truncate px-3 py-2 text-xs font-medium text-thstrong">{q.agent || data.agents[q.c]?.name || q.c}</td>
                  <td className="max-w-[130px] truncate px-3 py-2 text-xs text-thmute">{q.tl || data.agents[q.c]?.tl || "—"}</td>
                  <td className="px-3 py-2 text-xs text-thbody">{q.ch === "WA" ? "WhatsApp" : "Live Chat"}</td>
                  <td className="px-3 py-2 text-xs"><span style={{ color: ASSESS_COLORS[q.assessment] }}>{q.assessment}</span></td>
                  <td className="px-3 py-2 text-right font-mono-hud text-xs text-thbody">{fmtN(q.score, 0)}</td>
                  <td className="max-w-[240px] truncate px-3 py-2 text-xs text-thmute" title={q.errors.map((e) => `${e.cat}: ${e.err}`).join(" · ")}>
                    {q.errors.length ? q.errors.map((e) => e.err || e.cat).join(" · ") : "—"}
                  </td>
                  <td className="max-w-[200px] truncate px-3 py-2 text-xs text-thdim" title={q.tag}>{q.tag || "—"}</td>
                </tr>
              ))}
            </tbody>
          </table>
          {logRows.length === 0 && <p className="py-8 text-center text-sm text-thdim">No audits match.</p>}
        </div>
      </div>
    </div>
  );
}
